import React from "react";
import { motion } from "framer-motion";
import { Container } from "../ui";
import { cn } from "../../utils/cn";
import { PARTNER_REGISTER_URL } from "../../utils/appLinks";

export interface MerchantSectionProps {
  className?: string;
}

const merchantPills = [
  {
    title: "Claim Your Area",
    icon: (
      <svg className="w-4 h-4 text-purple-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    title: "Post and Promote",
    icon: (
      <svg className="w-4 h-4 text-purple-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
      </svg>
    ),
  },
  {
    title: "Welcome Customers",
    icon: (
      <svg className="w-4 h-4 text-purple-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
  {
    title: "Reward and Retain",
    icon: (
      <svg className="w-4 h-4 text-purple-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
];

const MerchantSection: React.FC<MerchantSectionProps> = ({ className }) => {
  return (
    <section
      id="merchants"
      className={cn(
        "py-16 sm:py-20 lg:py-28 bg-[#180A2D] relative overflow-hidden text-white",
        className
      )}
    >
      {/* Background ambient lighting */}
      <div className="absolute top-1/3 left-10 w-96 h-96 bg-purple-600/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />

      <div className="absolute inset-0 bg-line-grid pointer-events-none" aria-hidden="true" />

      <Container size="xl" padding="md" className="relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left Column: Headline, Subtitle, 4 Badges, CTAs */}
          <motion.div
            className="lg:col-span-6 text-center lg:text-left"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-extrabold text-white tracking-tight leading-[1.15]">
              Get discovered.
              <br />
              Build loyalty.
            </h2>

            <p className="text-purple-200/80 text-sm sm:text-base mt-3 mb-8 max-w-md mx-auto lg:mx-0 font-normal">
              Own your area. Let offers bring them back.
            </p>

            {/* 4 Pill Badges in 2x2 Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md mx-auto lg:mx-0 mb-8 sm:mb-10">
              {merchantPills.map((pill) => (
                <div
                  key={pill.title}
                  className="glass-dark inline-flex items-center justify-center sm:justify-start gap-2.5 px-4 py-2.5 rounded-full hover:bg-white/15 text-xs sm:text-sm font-semibold text-purple-100 transition-colors"
                >
                  {pill.icon}
                  <span>{pill.title}</span>
                </div>
              ))}
            </div>

            {/* Two Action Buttons */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-4">
              <a
                href={PARTNER_REGISTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="sheen inline-flex items-center justify-center px-7 sm:px-8 py-3.5 rounded-full text-xs sm:text-sm font-bold text-[#1E1B4B] bg-gradient-to-b from-[#FFD24D] to-[#F5B015] shadow-[0_12px_28px_-8px_rgba(251,191,36,0.55),inset_0_1px_0_rgba(255,255,255,0.5)] transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
              >
                List Your Business
              </a>

              <a
                href={PARTNER_REGISTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="glass-dark inline-flex items-center justify-center px-6 sm:px-7 py-3.5 rounded-full text-xs sm:text-sm font-semibold text-white hover:bg-white/15 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
              >
                Check availability
              </a>
            </div>
          </motion.div>

          {/* Right Column: Lavender Rounded Card with 3D Store Mockup */}
          <motion.div
            className="lg:col-span-6 flex justify-center items-center"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <div className="glass-dark w-full max-w-[520px] rounded-[36px] sm:rounded-[42px] p-2 sm:p-2.5">
            <div className="rounded-[30px] sm:rounded-[34px] bg-gradient-to-br from-[#F3EDFB] to-[#E4D9F4] p-6 sm:p-10 flex items-center justify-center shadow-[inset_0_1px_0_#fff] overflow-hidden">
              <img
                src="/get-discovered.png"
                alt="SetGo Local Store Area Exclusivity"
                className="w-full h-auto object-contain drop-shadow-xl select-none"
              />
            </div>
            </div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

export { MerchantSection };
export default MerchantSection;
